'use client'
import { RefObject, useEffect } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

interface PinOptions {
    // Ancho mínimo de viewport para activar el pin
    minWidth?: number;
    // Ancho mínimo de viewport para permitir pinear la segunda columna
    minWidthSecond?: number;
}

// Pinea la columna más corta de un layout de dos columnas hasta que la más
// larga termina de hacer scroll. Se re-mide y reconstruye cada vez que
// cambian las medidas de las columnas (resize, tipografía en vw, carga de
// imágenes lazy), y solo mata su propio trigger — nunca los del resto de la página.
export default function usePinShorterColumn(
    firstRef: RefObject<HTMLElement | null>,
    secondRef: RefObject<HTMLElement | null>,
    { minWidth = 768, minWidthSecond = 768 }: PinOptions = {}
) {
    useEffect(() => {
        const first = firstRef.current;
        const second = secondRef.current;
        if (!first || !second) return;

        let trigger: ScrollTrigger | null = null;
        let frame = 0;
        let lastSizes = '';

        const kill = () => {
            trigger?.kill();
            trigger = null;
        };

        const build = () => {
            kill();

            if (window.innerWidth < minWidth) return;

            const pinEl = first.offsetHeight <= second.offsetHeight ? first : second;
            const otherEl = pinEl === first ? second : first;

            if (pinEl === second && window.innerWidth < minWidthSecond) return;
            if (otherEl.offsetHeight - pinEl.offsetHeight <= 0) return;

            trigger = ScrollTrigger.create({
                trigger: pinEl,
                start: 'bottom bottom',
                // El pin se suelta exactamente cuando el final de la columna larga
                // llega abajo: anclado al elemento real, no a una distancia calculada,
                // para que el texto nunca invada los módulos siguientes
                endTrigger: otherEl,
                end: 'bottom bottom',
                pin: true,
                pinSpacing: false,
                invalidateOnRefresh: true,
            });
        };

        const observer = new ResizeObserver(() => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                const sizes = `${first.offsetWidth}x${first.offsetHeight}|${second.offsetWidth}x${second.offsetHeight}`;
                if (sizes === lastSizes) return;
                lastSizes = sizes;
                build();
                ScrollTrigger.refresh();
            });
        });

        observer.observe(first);
        observer.observe(second);

        // Los vídeos/imágenes lazy de OTROS módulos desplazan toda la página al
        // cargar y dejan los triggers apuntando a posiciones viejas (textos
        // superpuestos sobre los módulos siguientes). Cualquier cambio de altura
        // del documento debe recalcular los triggers.
        let bodyFrame = 0;
        let lastBodyHeight = -1;
        const bodyObserver = new ResizeObserver(() => {
            cancelAnimationFrame(bodyFrame);
            bodyFrame = requestAnimationFrame(() => {
                const h = document.body.offsetHeight;
                if (h === lastBodyHeight) return;
                lastBodyHeight = h;
                ScrollTrigger.refresh();
            });
        });
        bodyObserver.observe(document.body);

        return () => {
            cancelAnimationFrame(frame);
            cancelAnimationFrame(bodyFrame);
            observer.disconnect();
            bodyObserver.disconnect();
            kill();
        };
    }, [firstRef, secondRef, minWidth, minWidthSecond]);
}
